import { Close as CloseIcon } from '@mui/icons-material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { IPublication } from '@opengov/stories-shared/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { SpreadsheetsProcessingStatus_Enum } from '../../../../../gql-generated/hasura/graphql';
import { assertNotNil } from '../../../../../types/typeUtils';
import { uploadFileToS3 } from '../../../../../utils/s3';
import { useToast } from '../../../Toast/context/useToast.context';
import locale from '../customReports-locale.json';
import { customReportsQueryKeys } from '../hooks/customReportsQueryKeys';
import { useCreateSpreadsheet } from '../hooks/useCreateSpreadsheet';
import { getPresignedUrl } from '../hooks/useGetPresignedUrl';
import { useUpdateSpreadsheetForUploadedFile } from '../hooks/useUpdateSpreadsheetForUploadedFile';
import { FileSelectedStep } from './FileSelectedStep';
import { styles } from './NewExcelFileModal.styles';
import { UploadStep } from './UploadStep';

export type Mode = { variant: 'create' } | { variant: 'update'; spreadsheetId: string; initialReportName: string };

type Props = {
  publication: IPublication;
  mode: Mode;
  onClose: () => void;
};

type Step = 'upload' | 'file-selected';

export function NewExcelFileModal({ publication, mode, onClose }: Props) {
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customReportName, setCustomReportName] = useState(() => {
    return mode.variant === 'update' ? mode.initialReportName : '';
  });
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: createSpreadsheet } = useCreateSpreadsheet();
  const { mutateAsync: updateSpreadsheet } = useUpdateSpreadsheetForUploadedFile();
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const handleSubmit = async () => {
    setIsUploading(true);

    // Get spreadsheetId to use based on mode
    let spreadsheetIdToUse: string | undefined;

    try {
      if (!selectedFile) {
        throw new Error('No file selected');
      }

      if (mode.variant === 'update') {
        spreadsheetIdToUse = mode.spreadsheetId;
      } else {
        const { insert_spreadsheets_one } = await createSpreadsheet({
          entityId: publication.entityId,
          publicationId: publication.id,
          name: customReportName.length > 0 ? customReportName : assertNotNil(selectedFile).name
        });
        spreadsheetIdToUse = insert_spreadsheets_one?.id;
      }

      const spreadsheetId = assertNotNil(spreadsheetIdToUse, 'Spreadsheet ID not found');

      // Update spreadsheet with Uploaded status
      if (mode.variant === 'update') {
        await updateSpreadsheet({
          id: spreadsheetId,
          processingStatus: SpreadsheetsProcessingStatus_Enum.Uploaded,
          name: customReportName
        });
      }

      // Get presigned URL for S3 upload
      const { presignedUrl } = await getPresignedUrl({
        publicationId: publication.id,
        spreadsheetId,
        fileName: selectedFile.name
      });

      // Upload file to S3
      await uploadFileToS3({ presignedUrl, file: selectedFile });
      void queryClient.invalidateQueries({ queryKey: customReportsQueryKeys.all });
    } catch (error) {
      try {
        if (spreadsheetIdToUse) {
          await updateSpreadsheet({
            id: spreadsheetIdToUse,
            processingStatus: SpreadsheetsProcessingStatus_Enum.Failed,
            name: customReportName
          });
        }
      } catch (error) {
        console.error('File upload failed. Error updating spreadsheet with Failed Upload status', error);
      } finally {
        console.error(error);
        toggleToast({
          title: locale.errors.somethingWentWrong,
          body: locale.errors.unableToCreateCustomReport,
          severity: 'error'
        });
      }
    } finally {
      setIsUploading(false);
      onClose();
    }
  };

  const getModalTitleAndButtonText = () => {
    switch (mode.variant) {
      case 'create':
        return [locale.addNewExcelFileModal.newExcelFile, locale.addNewExcelFileModal.add];
      case 'update':
        return [locale.addNewExcelFileModal.updateExcelFile, locale.addNewExcelFileModal.updateAndRefreshData];
      default:
        const exhaustiveCheck: never = mode;
        console.error('Mode is not supported', exhaustiveCheck);
        return [locale.addNewExcelFileModal.newExcelFile, locale.addNewExcelFileModal.add];
    }
  };
  const [modalTitle, buttonText] = getModalTitleAndButtonText();

  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <UploadStep
            onFileSelected={(files) => {
              if (files.length > 0) {
                // Dropzone ensures only one file is selected
                setSelectedFile(files[0]);
                setCurrentStep('file-selected');
              }
            }}
          />
        );
      case 'file-selected':
        return (
          <FileSelectedStep
            mode={mode}
            selectedFile={assertNotNil(selectedFile)}
            customReportName={customReportName}
            setCustomReportName={setCustomReportName}
            onReUpload={() => {
              setCurrentStep('upload');
              setSelectedFile(null);
            }}
          />
        );
      default:
        const exhaustiveCheck: never = currentStep;
        return exhaustiveCheck;
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth={true}
      data-test="add-new-excel-file-modal"
      disableEscapeKeyDown
    >
      <DialogTitle sx={styles.dialogTitle}>
        {modalTitle}
        <Button
          variant="outlined"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={styles.actionButton}
          data-test="new-excel-file-modal-close-button"
        >
          {locale.addNewExcelFileModal.close}
        </Button>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent} dividers={true}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onClose} variant="outlined" sx={styles.actionButton} color="secondary">
          {locale.addNewExcelFileModal.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="outlined"
          sx={styles.actionButton}
          disabled={!customReportName || !selectedFile || isUploading}
          endIcon={isUploading ? <CircularProgress size={16} sx={styles.loadingIcon} /> : null}
          data-test="new-excel-file-modal-add-button"
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
